import api from './client'
import type { CreateTransactionPayload, EarmarkWarning } from './transactions'

export type RecurringType = 'INCOME' | 'EXPENSE'
export type Frequency = 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'HALF_YEARLY' | 'YEARLY'
export type RecurringStatus = 'active' | 'due_soon' | 'cancelled'
// Only meaningful for type=EXPENSE — the backend rejects a kind on INCOME items and
// requires one on EXPENSE (legacy rows without one read back as OTHER).
export type RecurringKind = 'BILL' | 'EMI' | 'SUBSCRIPTION' | 'OTHER'

export interface LoanProgress {
  outstanding: number
  tenureMonths: number | null
  monthsPaid: number
  emiAmount: number | null
}

export interface RecurringSkip {
  date: string
  reason: string | null
}

export type CycleAction = 'DONE' | 'MISSED' | 'RESCHEDULE'

// One action POST /recurring/:id/undo can reverse, newest first.
export interface RecurringUndoable {
  action: CycleAction
  dueDateBefore: string
  dueDateAfter: string
  createdAt: string
}

export interface RecurringSplit {
  contactId: string
  contactName: string | null
  contactArchived: boolean
  amount: number
}

export interface RecurringItem {
  id: string
  name: string
  type: RecurringType
  amount: number
  frequency: Frequency
  nextDueDate: string
  categoryId?: string
  categoryName?: string
  categoryIcon?: string
  financialAccountId?: string
  financialAccountName?: string
  // Optional: the frontend can deploy before the backend that returns them.
  loanAccountId?: string | null
  loanAccountName?: string | null
  splits?: RecurringSplit[]
  paidByContactId?: string | null
  paidByContactName?: string | null
  paidByContactArchived?: boolean
  notes?: string
  cancelledAt?: string
  monthlyEquivalent: number
  kind: RecurringKind | null
  icon: string | null
  loanProgress: LoanProgress | null
  skips: RecurringSkip[]
  undoable?: RecurringUndoable[]
  // Newest linked payment minus the one before it; null with fewer than two.
  lastPaymentChange?: number | null
  // The amount is an estimate (water, electricity) that follows the average of the last 3.
  amountVaries?: boolean
}

export interface RecurringHistoryEntry {
  kind: 'PAID' | 'MISSED'
  date: string
  amount: number | null
  accountName: string | null
  accountId: string | null
  principal: number | null
  interest: number | null
  reason: string | null
  transactionId: string | null
  // What the item expected when this was paid; null for older payments. Optional: the frontend
  // can deploy before the backend that returns it.
  expectedAmount?: number | null
}

export interface RecurringHistory {
  createdAt: string
  paidTotal: number
  paidCount: number
  entries: RecurringHistoryEntry[]
}

// Under ['recurring'], so every existing recurring invalidation refreshes it too.
export const recurringHistoryKey = (id: string) => ['recurring', 'history', id] as const

export function getRecurringHistory(id: string): Promise<RecurringHistory> {
  return api.get(`/recurring/${id}/history`).then((r) => r.data)
}

export interface KindBreakdown {
  kind: RecurringKind
  monthlyAmount: number
}

export interface RecurringSummary {
  monthlyPayments: number
  monthlyIncome: number
  yearlyPaymentProjection: number
  // Always all four kinds, fixed order (BILL, EMI, SUBSCRIPTION, OTHER), zero amount if none.
  paymentsByKind: KindBreakdown[]
}

export interface RecurringListResponse {
  items: RecurringItem[]
  summary: RecurringSummary
}

export interface CreateRecurringPayload {
  name: string
  type: RecurringType
  amount: number
  frequency: Frequency
  nextDueDate: string
  categoryId?: string
  financialAccountId?: string
  loanAccountId?: string
  notes?: string
  kind?: RecurringKind
  icon?: string
  // One or the other, never both — fixed at creation like the rest of the item.
  splits?: { contactId: string; amount: number }[]
  paidByContactId?: string
  amountVaries?: boolean
}

// Only name, icon, and notes can be edited after creation — the API rejects everything
// else with 400 (cancel the item and add a new one to change type/amount/frequency/
// nextDueDate/category/account/kind). The amount can also move via mark done's updateExpected.
export interface UpdateRecurringPayload {
  name?: string
  notes?: string | null
  icon?: string | null
  amountVaries?: boolean
}

export function listRecurring(
  status?: RecurringStatus,
  type?: RecurringType
): Promise<RecurringListResponse> {
  return api
    .get('/recurring', { params: { ...(status && { status }), ...(type && { type }) } })
    .then((r) => r.data)
}

export function createRecurring(payload: CreateRecurringPayload): Promise<RecurringItem> {
  return api.post('/recurring', payload).then((r) => r.data)
}

export function updateRecurring(
  id: string,
  payload: UpdateRecurringPayload
): Promise<RecurringItem> {
  return api.patch(`/recurring/${id}`, payload).then((r) => r.data)
}

// A linked EMI sends its principal/interest split; any other item may send what actually
// happened (date, amount, income's spend month) and whether that amount is expected from now on.
export interface MarkDonePayload {
  date?: string
  fromAccountId?: string
  principal?: number
  interest?: number
  amount?: number
  spendMonth?: string
  updateExpected?: boolean
}

export function markDone(id: string, payload?: MarkDonePayload): Promise<RecurringItem> {
  return api.post(`/recurring/${id}/mark-done`, payload).then((r) => r.data)
}

export function skipRecurring(id: string, reason?: string): Promise<RecurringItem> {
  return api.post(`/recurring/${id}/skip`, { reason: reason || undefined }).then((r) => r.data)
}

// The API can undo several steps (newest first, all or nothing); the UI offers one for now.
export function undoRecurring(id: string, steps = 1): Promise<RecurringItem> {
  return api.post(`/recurring/${id}/undo`, { steps }).then((r) => r.data)
}

export function rescheduleRecurring(id: string, nextDueDate: string): Promise<RecurringItem> {
  return api.post(`/recurring/${id}/reschedule`, { nextDueDate }).then((r) => r.data)
}

export function cancelRecurring(id: string): Promise<void> {
  return api.post(`/recurring/${id}/cancel`)
}

export function restoreRecurring(id: string): Promise<RecurringItem> {
  return api.post(`/recurring/${id}/restore`).then((r) => r.data)
}

// The kinds a reminder made from a transaction can take — EMI is set up from its loan instead.
export type RepeatKind = 'BILL' | 'SUBSCRIPTION' | 'OTHER'

export interface RecurringFromTransactionPayload {
  transaction: CreateTransactionPayload
  frequency: Frequency
  kind?: RepeatKind
  nextDueDate: string
}

export function createRecurringFromTransaction(
  payload: RecurringFromTransactionPayload
): Promise<{ recurringItem: RecurringItem; earmarkWarning: EarmarkWarning | null }> {
  return api.post('/recurring/from-transaction', payload).then((r) => r.data)
}
